// Admin Help & Setup page.
//
// Two parts:
//   1. Setup status — live checklist of "is this configured?" from
//      /api/admin/help/setup-status (green/red, with hints + links).
//   2. Searchable manual — the HELP_SECTIONS content, filtered by a
//      query, with click-to-expand blocks.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../../lib/api';
import { HELP_SECTIONS } from './content';

interface StatusItem {
  key: string;
  label: string;
  status: 'ok' | 'warn' | 'missing';
  hint: string;
  link?: string;
}

const STATUS_COLOR: Record<StatusItem['status'], string> = {
  ok: 'text-accent-success border-emerald-700/50 bg-emerald-900/20',
  warn: 'text-amber-300 border-amber-700/50 bg-amber-900/20',
  missing: 'text-red-300 border-red-700/50 bg-red-900/20',
};
const STATUS_DOT: Record<StatusItem['status'], string> = {
  ok: 'bg-emerald-400',
  warn: 'bg-amber-400',
  missing: 'bg-red-400',
};

function SetupStatus() {
  const { data, isPending, isError } = useQuery<{ items: StatusItem[] }>({
    queryKey: ['help-setup-status'],
    queryFn: () => api<{ items: StatusItem[] }>('/api/admin/help/setup-status'),
  });
  if (isPending) return <div className="text-sm text-ink-muted">Checking setup…</div>;
  if (isError || !data) return <div className="text-sm text-red-400">Couldn't check setup status.</div>;
  const okCount = data.items.filter((i) => i.status === 'ok').length;
  return (
    <div className="bg-bg-elev border border-border rounded p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-ink">Setup status</h2>
        <span className="text-xs text-ink-muted">{okCount}/{data.items.length} configured</span>
      </div>
      <ul className="space-y-2">
        {data.items.map((item) => (
          <li key={item.key} className={`flex items-start gap-3 px-3 py-2 rounded border text-sm ${STATUS_COLOR[item.status]}`}>
            <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${STATUS_DOT[item.status]}`} />
            <div>
              <div className="font-medium">{item.label}</div>
              <div className="text-xs opacity-80 mt-0.5">{item.hint}</div>
              {item.link && <Link to={item.link} className="text-xs underline mt-1 inline-block">Fix it</Link>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BlockBody({ block }: { block: { heading: string; steps: string[]; note?: string } }) {
  return (
    <div>
      <ol className="text-sm text-ink-muted space-y-1.5 mt-1 pl-5 list-decimal">
        {block.steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
      {block.note && <p className="text-sm text-amber-300/90 mt-2 pl-5">⚠️ {block.note}</p>}
    </div>
  );
}

function Manual({ query }: { query: string }) {
  const q = query.trim().toLowerCase();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const filtered = useMemo(() => {
    if (!q) return HELP_SECTIONS;
    return HELP_SECTIONS
      .map((s) => ({
        ...s,
        blocks: s.blocks.filter((b) => `${s.title} ${b.heading} ${b.steps.join(' ')} ${b.note ?? ''}`.toLowerCase().includes(q)),
      }))
      .filter((s) => s.blocks.length > 0 || s.title.toLowerCase().includes(q));
  }, [q]);

  if (filtered.length === 0) return <div className="text-sm text-ink-muted py-8 text-center">No results for "{query}".</div>;

  return (
    <div className="space-y-4">
      {filtered.map((section) => {
        const visibleBlocks = q ? section.blocks : section.blocks.filter((_, i) => open[`${section.id}-${i}`] || i === 0);
        return (
          <div key={section.id} className="bg-bg-elev border border-border rounded p-4">
            <h3 className="text-base font-semibold text-ink flex items-center gap-2">
              <span>{section.icon}</span> {section.title}
            </h3>
            <p className="text-sm text-ink-muted mt-1">{section.summary}</p>
            <div className="mt-3 space-y-2">
              {section.blocks.map((b, i) => {
                const isOpen = q || open[`${section.id}-${i}`] || i === 0;
                return (
                  <div key={i}>
                    {!q && (
                      <button
                        onClick={() => setOpen((o) => ({ ...o, [`${section.id}-${i}`]: !isOpen }))}
                        className="w-full text-left text-sm font-medium text-ink hover:text-brand flex items-center gap-2"
                      >
                        <span className="text-ink-muted">{isOpen ? '▾' : '▸'}</span> {b.heading}
                      </button>
                    )}
                    {isOpen && <BlockBody block={b} />}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AdminHelpPage() {
  const [query, setQuery] = useState('');
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-ink mb-1">Help &amp; Setup</h1>
      <p className="text-sm text-ink-muted mb-6">
        The admin manual. Check your setup is configured, or search how a feature works.
      </p>

      <SetupStatus />

      <div className="mt-6">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the manual… (e.g. adventure, boss, token, chronicle)"
          className="w-full px-3 py-2 bg-bg-elev border border-border rounded text-sm mb-4"
        />
        <Manual query={query} />
      </div>
    </div>
  );
}
